/**
 * Pure helpers for Explore catalog normalization and icon mapping.
 *
 * - Normalizes raw backend response into a stable frontend shape.
 * - Maps backend icon tokens to local component names.
 * - Injects the "all" category if backend omits it.
 * - Sorts categories and cards by their `order` field.
 */

import type {
  ExploreCatalogResponse,
  ExploreCatalogCategory,
  ExploreCatalogCard,
  NormalizedExploreCatalog,
} from '../types/explore.types'

/** All known icon tokens the backend can send, mapped to icon component names. */
const ICON_TOKEN_MAP: Record<string, string> = {
  package: 'PackageIcon',
  dollar: 'DollarIcon',
  'trend-up': 'TrendUpIcon',
  calendar: 'CalendarIcon',
  'bar-chart': 'BarChartIcon',
  'map-pin': 'MapPinIcon',
}

/** Fallback icon component name when the token is unknown. */
const FALLBACK_ICON = 'PackageIcon'

/**
 * Resolve a backend icon token to a local icon component name.
 * Returns `FALLBACK_ICON` for unrecognized tokens.
 */
export function resolveIconToken(token: string): string {
  return ICON_TOKEN_MAP[token] ?? FALLBACK_ICON
}

const ALL_CATEGORY: ExploreCatalogCategory = {
  id: 'all',
  label: 'All Queries',
  order: 0,
}

/**
 * Normalize a raw backend catalog response into a stable frontend shape.
 *
 * - Ensures the "all" category is always present at order 0.
 * - Sorts categories ascending by `order`.
 * - Filters out disabled cards.
 * - Sorts enabled cards ascending by `order`.
 */
export function normalizeExploreCatalog(raw: ExploreCatalogResponse): NormalizedExploreCatalog {
  const hasAll = raw.categories.some((c) => c.id === 'all')
  const rawCategories = hasAll ? raw.categories : [ALL_CATEGORY, ...raw.categories]

  const categories = [...rawCategories].sort((a, b) => a.order - b.order)

  const cards: ExploreCatalogCard[] = raw.cards
    .filter((c) => c.enabled)
    .sort((a, b) => a.order - b.order)

  return {
    tenantId: raw.tenantId,
    categories,
    cards,
  }
}

/**
 * Validate that a catalog card has all required initiation fields.
 * Returns true when the card can safely be used to build a first-load payload.
 */
export function isCardInitiationValid(card: ExploreCatalogCard): boolean {
  const { initiation } = card
  if (!initiation) return false
  if (initiation.type !== 'query' && initiation.type !== 'menu') return false
  if (!initiation.prompt) return false
  if (!initiation.currentMenuId) return false
  if (initiation.type === 'query' && !initiation.selectedMenuItemId) return false
  return true
}
